import { Alert, Button, Checkbox, Form, Row, message } from 'antd';
import DecorateTitle from 'components/DecorateTitle';
import LockHandler from 'components/LockHandler';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import useNavigateWithQuery from 'hooks/useNavigateWithQuery';
import { ReactNode, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import { updateUserConfig } from 'services/api/admin';
import { openExternalURL } from 'utils/openExternalURL';
import { ReceptionConfig } from 'views/Aide/Qa/NightWatch/ReceptionForm';
import { useReceptionConfig, useSetReceptionConfig } from './useReceptionConfig';
import { useNewQAEnable } from 'hooks/useNewQAEnable';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';

function ConversationSetting({ onClose }: { onClose?: Function }) {
  const { t } = useTranslate();

  const nav = useNavigateWithQuery();

  const language = useSystemLanguageValue();

  const newQAEnable = useNewQAEnable();
  const { whiteListConfig: { UNPROCESSED_WITH_BOT_REPLIED = false } = {} } =
    useOrgConfigStateValue();

  const getPermission = useGetPermission();

  const receptionConfig = useReceptionConfig();

  const setReceptionConfig = useSetReceptionConfig();

  const { data: userConfig } = useQuery({
    ...queries.accounts.userConfig,
    enabled: !receptionConfig,
  });

  useEffect(() => {
    if (userConfig?.receptionConfig) {
      setReceptionConfig(userConfig.receptionConfig);
    }
  }, [userConfig?.receptionConfig, setReceptionConfig]);

  if (!receptionConfig) {
    return null;
  }

  const receptionRender = (name: keyof General.BuyerReceptionConfig, title: ReactNode) => {
    return (
      <div>
        <h5 style={{ marginBottom: '6px', fontSize: '14px' }}>{title}</h5>
        <ReceptionConfig
          value={receptionConfig[name] as General.SingleReceptionConfig}
          onChange={(value) => {
            const next = {
              ...receptionConfig,
              [name]: value,
            };
            setReceptionConfig(next);
            updateUserConfig(next).catch(() => {
              message.error(t('operation.saveFailed'));
            });
          }}
        />
      </div>
    );
  };

  return (
    <div>
      <Row align="middle" justify="space-between">
        <DecorateTitle style={{ marginBottom: 0 }}>{t('in_reception_setting')}</DecorateTitle>
        <div>
          {newQAEnable && getPermission('AI智能接待') && (
            <Button
              type="text"
              onClick={() => {
                nav('/aide/qa/groupSetting');
                onClose?.();
              }}
            >
              {t('remind.去配置x', { x: t('criteria_for_post_sale') })}
            </Button>
          )}
          <Button
            type="text"
            onClick={() =>
              openExternalURL(
                language === 'zh-CN'
                  ? 'https://leyan.yuque.com/czpqnb/nmiwyb'
                  : 'https://chatplus.ganjutech.com/wiki/en/',
              )
            }
            style={{ paddingRight: 0 }}
          >
            {t('aide.使用说明')}
          </Button>
        </div>
      </Row>
      <div
        style={{
          marginBottom: '12px',
          padding: '8px 12px',
          background: 'var(--fett-bg-info-tip)',
          borderRadius: '2px',
        }}
      >
        {t('buyer_type_tips')}
      </div>
      <Form layout="vertical" colon>
        {receptionRender('preSaleBuyer', t('aide.售前买家'))}
        {receptionRender('inSaleBuyer', t('aide.售中买家'))}
        {receptionRender('afterSaleBuyer', t('aide.售后买家'))}
      </Form>
      {!UNPROCESSED_WITH_BOT_REPLIED && (
        <div>
          <DecorateTitle style={{ marginBottom: 0 }}>{t('to_be_processed_setting')}</DecorateTitle>
          <div>
            <LockHandler
              Component={Checkbox}
              handlerKey="onChange"
              checked={receptionConfig.isDisplayTobeCheckedConversation}
              onChange={({ target: { checked: isDisplayTobeCheckedConversation } }: any) => {
                const next = { ...receptionConfig, isDisplayTobeCheckedConversation };

                setReceptionConfig(next);

                return updateUserConfig(next).catch(() => {
                  message.error(t('operation.saveFailed'));

                  setReceptionConfig(receptionConfig);
                });
              }}
              style={{ margin: '12px 0' }}
            >
              {t('to_be_processed_setting_content')}
            </LockHandler>
            {receptionConfig.isDisplayTobeCheckedConversation && (
              <Alert
                banner
                showIcon={false}
                type="info"
                message={t('to_be_processed_setting_tips')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ConversationSetting;
